// @ts-strict-ignore
import { Box } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import { analyticsMessages } from "../../messages";
import { ChannelPerformance } from "../../models/analytics";
import { EmptyState } from "../EmptyState";
import { seriesColors, surfaceCardStyle } from "./tokens";

interface ChannelPerformanceTableProps {
  title: string;
  description?: string;
  rows: ChannelPerformance[];
  columns: {
    channel: string;
    orders: string;
    gmv: string;
    aov: string;
    share: string;
    refundRate: string;
  };
  formatMoneyFor: (amount: number, currency: string) => string;
  onRowClick?: (row: ChannelPerformance) => void;
  headerActions?: React.ReactNode;
  loading?: boolean;
  na?: boolean;
  emptyMessage?: string;
}

const refundColor = (rate: number) => {
  if (rate >= 0.1) return "#DC2626";

  if (rate >= 0.05) return "#F59E0B";

  return "#10B981";
};

export const ChannelPerformanceTable: React.FC<ChannelPerformanceTableProps> = ({
  title,
  description,
  rows,
  columns,
  formatMoneyFor,
  onRowClick,
  headerActions,
  loading,
  na,
  emptyMessage,
}) => {
  const intl = useIntl();
  const empty = !na && !loading && (!rows || rows.length === 0);

  return (
    <Box padding={5} style={{ ...surfaceCardStyle }}>
      <Box marginBottom={4} display="flex" justifyContent="space-between" alignItems="flex-start" gap={3}>
        <Box>
          <Box as="div" fontSize={2} color="default2">
            {title}
          </Box>
          {description && (
            <Box as="div" fontSize={1} color="default2" marginTop={1} style={{ opacity: 0.8 }}>
              {description}
            </Box>
          )}
        </Box>
        {headerActions}
      </Box>

      {na ? (
        <Box padding={6} textAlign="center" color="default2">
          {intl.formatMessage(analyticsMessages.commonNA)}
        </Box>
      ) : loading ? (
        <Box padding={6} textAlign="center" color="default2">
          {intl.formatMessage(analyticsMessages.commonLoading)}
        </Box>
      ) : empty ? (
        <EmptyState message={emptyMessage ?? intl.formatMessage(analyticsMessages.commonNoData)} />
      ) : (
        <Box as="div" overflowX="auto">
          <table
            style={{
              width: "100%",
              borderCollapse: "separate",
              borderSpacing: 0,
              fontSize: 13,
            }}
          >
            <thead>
              <tr>
                {[
                  { label: columns.channel, align: "left" as const, w: undefined },
                  { label: columns.orders, align: "right" as const, w: 96 },
                  { label: columns.gmv, align: "right" as const, w: 160 },
                  { label: columns.aov, align: "right" as const, w: 130 },
                  { label: columns.refundRate, align: "right" as const, w: 110 },
                  { label: columns.share, align: "right" as const, w: 200 },
                ].map(h => (
                  <th
                    key={h.label}
                    style={{
                      textAlign: h.align,
                      padding: "8px 12px",
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#6B7280",
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
                      width: h.w,
                    }}
                  >
                    {h.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const color = seriesColors[i % seriesColors.length];
                const sharePct = (r.share * 100).toFixed(1);
                const bottomBorder =
                  i === rows.length - 1 ? "none" : "1px solid rgba(15, 23, 42, 0.06)";

                return (
                  <tr
                    key={`${r.channel}-${i}`}
                    onClick={onRowClick ? () => onRowClick(r) : undefined}
                    style={{
                      transition: "background 120ms ease",
                      cursor: onRowClick ? "pointer" : "default",
                    }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLTableRowElement).style.background =
                        "rgba(15, 23, 42, 0.025)";
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLTableRowElement).style.background = "transparent";
                    }}
                  >
                    <td style={{ padding: "10px 12px", borderBottom: bottomBorder }}>
                      <Box display="flex" alignItems="center" gap={3}>
                        <Box
                          as="span"
                          __width={10}
                          __height={10}
                          borderRadius={1}
                          style={{ background: color, flexShrink: 0 }}
                        />
                        <Box
                          as="span"
                          style={{
                            color: "#111827",
                            fontWeight: 600,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {r.channel || "—"}
                        </Box>
                        <Box
                          as="span"
                          paddingX={2}
                          borderRadius={2}
                          style={{
                            background: "rgba(15, 23, 42, 0.06)",
                            color: "#6B7280",
                            fontSize: 10,
                            fontWeight: 600,
                            letterSpacing: 0.3,
                          }}
                        >
                          {r.currency}
                        </Box>
                      </Box>
                    </td>
                    <td
                      style={{
                        padding: "10px 12px",
                        textAlign: "right",
                        fontFeatureSettings: '"tnum"',
                        color: "#374151",
                        borderBottom: bottomBorder,
                      }}
                    >
                      {r.orders}
                    </td>
                    <td
                      style={{
                        padding: "10px 12px",
                        textAlign: "right",
                        fontFeatureSettings: '"tnum"',
                        color: "#111827",
                        fontWeight: 700,
                        borderBottom: bottomBorder,
                      }}
                    >
                      {formatMoneyFor(r.gmv, r.currency)}
                    </td>
                    <td
                      style={{
                        padding: "10px 12px",
                        textAlign: "right",
                        fontFeatureSettings: '"tnum"',
                        color: "#374151",
                        borderBottom: bottomBorder,
                      }}
                    >
                      {formatMoneyFor(r.aov, r.currency)}
                    </td>
                    <td
                      style={{
                        padding: "10px 12px",
                        textAlign: "right",
                        fontFeatureSettings: '"tnum"',
                        color: refundColor(r.refundRate),
                        fontWeight: 700,
                        borderBottom: bottomBorder,
                      }}
                    >
                      {(r.refundRate * 100).toFixed(1)}%
                    </td>
                    <td style={{ padding: "10px 12px", borderBottom: bottomBorder }}>
                      <Box display="flex" alignItems="center" justifyContent="flex-end" gap={3}>
                        <Box
                          __width={100}
                          __height={6}
                          borderRadius={1}
                          style={{
                            background: "rgba(15, 23, 42, 0.06)",
                            overflow: "hidden",
                            flexShrink: 0,
                          }}
                        >
                          <Box
                            __height={6}
                            style={{
                              width: `${Math.min(100, r.share * 100)}%`,
                              background: color,
                              borderRadius: 1,
                            }}
                          />
                        </Box>
                        <Box
                          as="span"
                          style={{
                            fontWeight: 700,
                            color: "#111827",
                            fontFeatureSettings: '"tnum"',
                            minWidth: 48,
                            textAlign: "right",
                          }}
                        >
                          {sharePct}%
                        </Box>
                      </Box>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      )}
    </Box>
  );
};
